import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '../../errors';
import { useSavedViews } from '../../shell/savedViews';
import { useConfirm, useToast } from '../../ui';
import { ArrowDownIcon, ArrowUpIcon, EditIcon, TrashIcon } from '../../ui/icons';
import { ViewNameDialog } from './SavedViewDialogs';

/**
 * What a saved view's "…" menu does — rename, move up or down, delete
 * (asked first) — for the sidebar and the collection page alike. Render
 * `dialog` once; `items(view)` gives the menu's entries.
 */
export function useViewActions(inventoryId) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const toast = useToast();
  const { views, update, remove, move } = useSavedViews(inventoryId);
  const [renaming, setRenaming] = useState(null);

  const failed = (error) => toast.error(t('views.failed', { error: errorMessage(t, error) }));

  const deleteView = async (view) => {
    if (!(await confirm({
      title: t('views.deleteTitle', { name: view.name }),
      message: t('views.deleteText'),
      confirmLabel: t('views.delete'),
      danger: true,
    }))) return;
    try {
      await remove(view.id);
      toast.success(t('views.deleted', { name: view.name }));
    } catch (error) {
      failed(error);
    }
  };

  const items = (view) => {
    const list = views || [];
    const index = list.findIndex((candidate) => candidate.id === view.id);
    return [
      { id: 'rename', label: t('views.rename'), icon: EditIcon, onSelect: () => setRenaming(view) },
      index > 0 && { id: 'up', label: t('views.moveUp'), icon: ArrowUpIcon, onSelect: () => move(view.id, -1).catch(failed) },
      index >= 0 && index < list.length - 1
        && { id: 'down', label: t('views.moveDown'), icon: ArrowDownIcon, onSelect: () => move(view.id, 1).catch(failed) },
      'separator',
      { id: 'delete', label: t('views.delete'), icon: TrashIcon, danger: true, onSelect: () => deleteView(view) },
    ].filter(Boolean);
  };

  const dialog = (
    <ViewNameDialog
      open={!!renaming}
      title={t('views.renameTitle')}
      initialName={renaming ? renaming.name : ''}
      submitLabel={t('views.rename')}
      onSubmit={async (name) => {
        if (name !== renaming.name) {
          await update(renaming.id, { name });
          toast.success(t('views.renamed', { name }));
        }
        setRenaming(null);
      }}
      onClose={() => setRenaming(null)}
    />
  );

  return { items, dialog };
}

export default useViewActions;
